/**
 * Typed workflow step card.
 *
 * Renders a single workflow step from a tco_workflow_template with
 * type-specific detail (channel + priority for outreach, service category
 * for connect_provider, HEDIS measure for care_gap_check, etc.) instead of
 * a generic "manual task" placeholder. The card also surfaces type-specific
 * actions — most importantly, outreach_message steps get an
 * "Open in Comms Hub" link instead of opening a standalone modal.
 */
import React from 'react';
import {
  MessageSquare, Users, Target, FileCheck, Calendar, Clock,
  AlertTriangle, TrendingUp, ExternalLink,
} from 'lucide-react';
import { MEMBER_INBOX_TAB, requestOpenMemberInbox } from '@/lib/memberInboxNavigation';

export interface TypedStep {
  step: number;
  name: string;
  step_type?: string;
  type?: string;                  // legacy field name; preserved for back-compat
  config?: Record<string, any>;
  action?: string;                // legacy free-form action
}

const STEP_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  outreach_message:     MessageSquare,
  connect_provider:     Users,
  care_gap_check:       Target,
  collect_consent:      FileCheck,
  schedule_appointment: Calendar,
  wait_response:        Clock,
  escalate:             AlertTriangle,
  record_outcome:       TrendingUp,
};

const STEP_LABEL: Record<string, string> = {
  outreach_message:     'Outreach message',
  connect_provider:     'Connect to provider',
  care_gap_check:       'Care-gap check',
  collect_consent:      'Collect consent',
  schedule_appointment: 'Schedule appointment',
  wait_response:        'Wait for response',
  escalate:             'Escalate to CSR',
  record_outcome:       'Record outcome',
};

const STEP_COLOR: Record<string, string> = {
  outreach_message:     'border-blue-200 bg-blue-50 text-blue-700',
  connect_provider:     'border-indigo-200 bg-indigo-50 text-indigo-700',
  care_gap_check:       'border-emerald-200 bg-emerald-50 text-emerald-700',
  collect_consent:      'border-violet-200 bg-violet-50 text-violet-700',
  schedule_appointment: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  wait_response:        'border-amber-200 bg-amber-50 text-amber-700',
  escalate:             'border-rose-200 bg-rose-50 text-rose-700',
  record_outcome:       'border-teal-200 bg-teal-50 text-teal-700',
};

interface Props {
  step: TypedStep;
  memberId?: number | null;
  /** Called when the user clicks "Open in Comms Hub" on an outreach step.
   *  Caller should navigate to the comms hub conversation for memberId,
   *  pre-filling channel + template from step.config. If not provided,
   *  the card falls back to a default handler that opens the member's
   *  conversation in the member Inbox (TRUS-709). */
  onOpenCommsHub?: (memberId: number, config: Record<string, any>) => void;
  compact?: boolean;
}

function _defaultOpenCommsHub(memberId: number, config: Record<string, any>) {
  // TRUS-709: "Open in Comms Hub" opens the member's conversation in the
  // member Inbox (the 2-Way Inbox is retired). The app shell listens for
  // app-open-comms-hub (see app.tsx), parks the member and switches to the
  // Inbox, which opens the conversation. The payload keeps the step's config
  // (channel, template, priority) for listeners that use it.
  const prefill = {
    member_id: memberId,
    requested_at: new Date().toISOString(),
    ...config,
  };
  let handled = false;
  const evt = new CustomEvent('app-open-comms-hub', {
    detail: prefill,
    cancelable: true,
  });
  try { handled = !window.dispatchEvent(evt); } catch { handled = false; }
  if (!handled) {
    // No shell listening: park the member and load the Inbox (the app
    // restores the saved tab; the Inbox takes the parked member on mount).
    requestOpenMemberInbox(memberId);
    try { localStorage.setItem('activeTab', MEMBER_INBOX_TAB); } catch { /* private mode */ }
    const url = new URL(window.location.href);
    url.searchParams.set('tab', MEMBER_INBOX_TAB);
    url.searchParams.set('member_id', String(memberId));
    window.location.href = url.toString();
  }
}

export const TypedStepCard: React.FC<Props> = ({ step, memberId, onOpenCommsHub, compact }) => {
  const type = step.step_type || step.type || 'manual';
  const Icon = STEP_ICON[type] || MessageSquare;
  const label = STEP_LABEL[type] || (step.action ? step.action.replace(/_/g, ' ') : 'Manual task');
  const colorClasses = STEP_COLOR[type] || 'border-gray-200 bg-gray-50 text-gray-600';
  const config = step.config || {};

  return (
    <div className={`rounded-md border ${colorClasses} px-3 py-2`}>
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{step.name}</span>
            <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
              {label}
            </span>
          </div>
          {!compact && (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {type === 'outreach_message' && (
                <>
                  {config.channel && <span>📣 {config.channel}</span>}
                  {config.priority && <span>⚑ {config.priority}</span>}
                  {config.preferred_language && <span>🌐 {config.preferred_language}</span>}
                  {config.open_in_inbox && <span>📥 surfaces in CSR Inbox</span>}
                </>
              )}
              {type === 'connect_provider' && (
                <>
                  {config.service_category && <span>🎯 {config.service_category}</span>}
                  {config.auto_match && <span>🤖 auto-match</span>}
                  {config.max_providers != null && <span>≤{config.max_providers} providers</span>}
                </>
              )}
              {type === 'care_gap_check' && (
                <>
                  {config.measure_id_hedis && <span>📊 {config.measure_id_hedis}</span>}
                  {config.watch_for && <span>↳ {config.watch_for.replace(/_/g, ' ')}</span>}
                </>
              )}
              {type === 'collect_consent' && (
                <>
                  {config.category_key && <span>🔐 {config.category_key}</span>}
                  {config.required && <span>⚠ required</span>}
                </>
              )}
              {type === 'schedule_appointment' && (
                <>
                  {config.appointment_type && <span>📅 {config.appointment_type}</span>}
                  {config.lead_time_hours && <span>⏱ {config.lead_time_hours}h lead-time</span>}
                </>
              )}
              {type === 'wait_response' && (
                <>
                  {config.timeout_hours && <span>⏳ {config.timeout_hours}h timeout</span>}
                  {config.on_timeout && <span>↳ {config.on_timeout}</span>}
                </>
              )}
              {type === 'escalate' && (
                <>
                  {config.queue && <span>📞 queue: {config.queue}</span>}
                  {config.priority && <span>⚑ {config.priority}</span>}
                </>
              )}
              {type === 'record_outcome' && (
                <>
                  {config.outcome_name && <span>📈 {config.outcome_name}</span>}
                  {config.value != null && <span>value: {config.value}</span>}
                </>
              )}
            </div>
          )}
        </div>
        {type === 'outreach_message' && memberId && (
          <button
            onClick={() => (onOpenCommsHub || _defaultOpenCommsHub)(memberId, config)}
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-blue-300 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
            title="Send via the Communications Hub — flows into the same case-management conversation, no standalone modal"
          >
            <ExternalLink className="h-3 w-3" />
            Open in Comms Hub
          </button>
        )}
      </div>
    </div>
  );
};

export default TypedStepCard;
